// Construit, de façon pure (sans dispatch/Redux), la liste des messages à
// afficher au conseiller à partir de la réponse backend de
// createOrUpdateSavOrder. Extrait de SavOrderForm.jsx (handleErrors) pour
// être testable indépendamment des 5 hooks SWR et du store Redux dont
// dépend le composant.
//
// Le backend (crmv3-backend) est en cours de correction pour propager le
// motif métier réel (ex: rupture de stock) dans `errorInfos.message` au lieu
// de renvoyer `200 null`. Tant que ce n'est pas garanti sur tous les
// endpoints, on affiche ce motif quand il est exploitable et on ne retombe
// sur le message générique que par défaut : on ne suppose jamais un format
// de réponse non vérifié.

// Formate un élément d'une liste d'erreurs backend en texte lisible pour un
// conseiller. Le corps 400 du BO sur un refus de stock est une LISTE
// D'OBJETS { reference, available_quantity, asked_quantity, error_message }
// (hoortrade-bo modules/order/logic.py:715-722 + serializers.py:1796,
// ValidationError(error_messages)) — jamais une liste de chaînes. On
// privilégie `error_message`, complété par la référence produit et les
// quantités (utiles au conseiller), et on ne devine jamais de format pour un
// objet qui n'a pas ce champ : mieux vaut renvoyer `null` (repli sur le
// message générique côté appelant) que d'afficher l'objet brut
// ("[object Object]").
const formatBackendMessageItem = (item) => {
  if (typeof item === 'string') {
    const trimmed = item.trim();
    if (trimmed.length === 0 || trimmed.toLowerCase() === 'null') {
      return null;
    }
    return trimmed;
  }

  if (item && typeof item === 'object' && !Array.isArray(item)) {
    const { reference, available_quantity, asked_quantity, error_message } =
      item;
    if (
      typeof error_message !== 'string' ||
      error_message.trim().length === 0
    ) {
      return null;
    }
    const parts = [];
    if (typeof reference === 'string' && reference.trim().length > 0) {
      parts.push(reference.trim());
    }
    if (available_quantity !== undefined && asked_quantity !== undefined) {
      parts.push(
        `(stock disponible : ${available_quantity}, demandé : ${asked_quantity})`
      );
    }
    parts.push(error_message.trim());
    return parts.join(' ');
  }

  return null;
};

// Normalise `errorInfos.message`, qui peut être une string, une liste (de
// chaînes et/ou d'objets d'erreur de stock), ou un objet DRF
// ({ non_field_errors: [...] }, { field: [...] }, ...). Retourne `null`
// quand aucun texte exploitable n'en ressort (absent, vide, "null", liste
// d'objets sans champ utilisable).
export const extractBackendMessage = (rawMessage) => {
  if (typeof rawMessage === 'string') {
    const trimmed = rawMessage.trim();
    if (trimmed.length === 0 || trimmed.toLowerCase() === 'null') {
      return null;
    }
    return trimmed;
  }

  if (Array.isArray(rawMessage)) {
    const joined = rawMessage
      .map(formatBackendMessageItem)
      .filter((item) => typeof item === 'string' && item.length > 0)
      .join(' ; ');
    return joined.length > 0 ? joined : null;
  }

  if (rawMessage && typeof rawMessage === 'object') {
    const joined = Object.values(rawMessage)
      .flat()
      .map(formatBackendMessageItem)
      .filter((item) => typeof item === 'string' && item.length > 0)
      .join(' ; ');
    return joined.length > 0 ? joined : null;
  }

  return null;
};

// Construit la liste ordonnée des snackbars à déclencher pour une réponse
// donnée, ainsi que `hasBlockingError` (4xx/5xx) pour que les appelants
// interrompent la chaîne (pas de faux succès / confirm).
export const buildSavOrderMessages = (data) => {
  const errorStatus = data?.errorInfos?.status;
  const rawMessage = data?.errorInfos?.message;
  const infoCode = data?.infoInfos?.code;
  const infoMessage = data?.infoInfos?.message;

  const backendMessage = extractBackendMessage(rawMessage);
  const messageTextForMatch = backendMessage ?? '';

  const is4xx = errorStatus >= 400 && errorStatus < 500;
  const is5xx = errorStatus >= 500;

  const messages = [];

  if (infoCode === 'SAV_ORDER_RELINKED_FROM_CANCELED' && infoMessage) {
    messages.push({ message: infoMessage, color: 'info' });
  }

  // Cas spécifique iso backend (CreateSAVOrdersSerializer.validate) : une SAV
  // encore "ouverte" (recorded/pending) chez Yusen/Noyon existe déjà pour
  // cette commande. Le BO renvoie un message contenant "already created".
  if (is4xx && /already created/i.test(messageTextForMatch)) {
    messages.push({
      message:
        'Une SAV est déjà en cours (Yusen/Noyon) pour cette commande. Gérez ou supprimez la SAV existante avant d’en créer une nouvelle.',
      color: 'warning',
    });
    return { messages, hasBlockingError: true };
  }

  if (is4xx) {
    // On affiche le motif métier renvoyé par le backend quand il existe et
    // qu'il est exploitable (ex: "STI-000080, available_quantity 0, le
    // niveau de stock virtuel est insuffisant"). Le message générique n'est
    // qu'un repli, pas le comportement par défaut.
    const messageError =
      backendMessage ??
      `Vérifiez l'association des pièces et du transporteur avant de confirmer.`;
    messages.push({ message: messageError, color: 'warning' });
  }

  if (is5xx) {
    messages.push({
      message: `Une erreur est survenue lors de la mise à jour du brouillon.`,
      color: 'error',
    });
  }

  if (data?.lines?.some((l) => !l.isValid)) {
    messages.push({
      message: `Une des pièces ajoutées est une référence de produit invalide.`,
      color: 'warning',
    });
  }

  return { messages, hasBlockingError: is4xx || is5xx };
};

// Sévérité utilisée par aggregateSavOrderMessages pour retenir la couleur la
// plus grave quand plusieurs messages sont fusionnés en un seul snackbar.
const COLOR_SEVERITY = { error: 3, warning: 2, info: 1 };

// Agrège la liste de messages produite par buildSavOrderMessages en un seul
// objet { message, color } affichable dans l'unique snackbar disponible.
//
// Pourquoi : staticSlice.setSnackBar fait `state.snackBar = action.payload`
// (remplacement pur, pas de file) et Header.jsx n'affiche qu'un seul
// <Snackbar>. handleErrors (SavOrderForm.jsx) dispatchait un setSnackBar par
// message dans le même tick synchrone : seul le dernier restait visible. Un
// refus métier 4xx (motif backend, warning) suivi de l'avertissement "une
// des pièces ajoutées est une référence de produit invalide" (warning, poussé
// après le motif métier) faisait donc disparaître le motif métier backend —
// exactement ce que cette MR corrige par ailleurs.
//
// Ni buildSavOrderMessages (module pur, ne change pas) ni le reducer
// staticSlice ni Header.jsx ne sont modifiés ici : une vraie file de
// snackbars est un changement d'architecture partagée (état Redux global) —
// hors périmètre de cette MR, à ouvrir en ticket séparé.
//
// Conserve l'ordre de production des messages (concaténation) et retient la
// couleur la plus sévère (error > warning > info) pour ne jamais dégrader la
// gravité affichée au conseiller.
export const aggregateSavOrderMessages = (messages) => {
  if (!messages || messages.length === 0) {
    return null;
  }

  if (messages.length === 1) {
    return messages[0];
  }

  const message = messages.map(({ message: text }) => text).join(' ');
  const color = messages.reduce((mostSevereColor, { color: currentColor }) => {
    const currentSeverity = COLOR_SEVERITY[currentColor] ?? 0;
    const mostSevereSeverity = COLOR_SEVERITY[mostSevereColor] ?? 0;
    return currentSeverity > mostSevereSeverity
      ? currentColor
      : mostSevereColor;
  }, messages[0].color);

  return { message, color };
};
